// Import necessary libraries

import { createMachine, interpret, assign, send, raise } from "xstate";
import { Category, fetchBooks, selectRandomBooks } from "../nyt";
import { getRandomElements } from "../utils/getRandomValuesFromList";
import { sortBooks } from "../openAi";
import { hydrateBooksAndCategories } from "./hydrateBooksAndCategories";
import { state } from "../state";
import sortCorrectAndIncorrectAnswers, {
  Answer,
} from "../utils/sortCorrectAndIncorrectAnswers";
import getReasonsForIncorrectAnswers from "../utils/getReasonsForIncorrectAnswers";

export interface HydratedBook {
  title: string;
  id: string;
  image: string;
  author: string;
  description: string;
}

// Define the schema for the state machine
interface GameContext {
  gameIsOver: boolean;
  pickedDate: string;
  // nytBookDate: string;
  nytCategoryNames: string[];
  booksFetchedFromNYT: NYTBook[];
  booksSelectedAtRandom: NYTBook[];
  booksCategorizedByOpenAI: Category[];
  finalizedBooks: HydratedBook[];
  finalizedCategories: string[];
  booksToSelectFrom: HydratedBook[];
  secondRoundHand: HydratedBook[];
  userSelections: HydratedBook[][];
  answers: Answer[];
  score: number;
}

type RestartEvent = { type: "RESTART"; invoke: { src: string } };
type FetchEvent = { type: "FETCH" };
type SelectEvent = {
  type: "SELECT";
  bookId: string;
  categoryName: string;
  fromCategoryIndex?: number;
};
type FinishEvent = { type: "FINISH" };

type GameEvent = FetchEvent | SelectEvent | FinishEvent | RestartEvent;

export interface GameStateSchema {
  states: {
    init: {};
    fetchingData: [];
    selectingBooks: {};
    sortingBooks: {};
    bookCategories: {};
    sorted: {};
  };
  context: GameContext;
  guards: {
    validSelection: (context: GameContext, event: SelectEvent) => boolean;
  };
}

type GameTypeState =
  | { value: "init"; context: GameContext }
  | { value: "fetchingData"; context: GameContext }
  | { value: "selectingBooks"; context: GameContext }
  | { value: "sortingBooks"; context: GameContext }
  | { value: "sorted"; context: GameContext }
  | { value: "initComplete"; context: GameContext }
  | { value: "active"; context: GameContext }
  | { value: "RESTART"; context: GameContext };

const isGameOver = (context: GameContext): boolean => {
  console.log({
    isEnd: context.booksToSelectFrom.length === 0,
    length: context.booksToSelectFrom.length,
  });
  return context.booksToSelectFrom.length === 0;
};

const validSelection = (context: GameContext, event: GameEvent): boolean => {
  console.log({
    context,
    event,
  });
  if (event.type !== "SELECT") {
    console.log("not a select event");
    return false;
  }

  if (!event.bookId || !event.categoryName) {
    console.log({ event, message: "does not have bookId or categoryName" });
    return false;
  }

  const categoryIndex = context.finalizedCategories.indexOf(event.categoryName);

  const bookIndex = context.booksToSelectFrom.findIndex(
    (book) => book.id === event.bookId
  );
  if (categoryIndex === undefined && bookIndex === undefined) {
    console.log({
      categoryIndex,
      bookIndex,
      message: "does not have category or book index",
    });
    return false;
  }
  // if (!bookIndex && !) return false;

  return true;
};

// Configure the state machine
export const gameMachine = createMachine<GameContext, GameEvent, GameTypeState>(
  {
    id: "game",
    // initial: "initComplete",
    initial: "startInit",
    // context: mockState,
    context: state,
    states: {
      failed: {
        type: "final",
        entry: (context) => console.log("failed", context),
      },
      startInit: {
        on: {
          FETCH: "fetchingData",
        },
      },
      fetchingData: {
        invoke: {
          src: "fetchBooks",
          onDone: {
            target: "sorted",
            actions: assign({
              // nytBookDate: (_, event) => event.data.selectedDate,
              booksCategorizedByOpenAI: (_, event) => event.data.categories,
              pickedDate: (_, event) => event.data.pickedDate,
            }),
          },
          onError: "failed",
        },
      },
      sorted: {
        invoke: {
          src: "hydrateBooksAndCategories",
          onDone: {
            target: "createHand",
            actions: assign({
              finalizedBooks: (_, event) => event.data.sortedBooks,
              finalizedCategories: (_, event) => event.data.selectedCategories,
            }),
          },
        },
      },
      createHand: {
        invoke: {
          src: "generateUserSelectionArrays",
          onDone: {
            actions: assign({
              userSelections: (_, event) => event.data.userSelections,
              booksToSelectFrom: (_, event) => event.data.booksToSelect,
              secondRoundHand: (_, event) => event.data.secondRoundHand,
            }),
            target: "initComplete",
          },
        },
      },
      initComplete: {
        entry: (context) => console.log("Final context:", context),
        on: {
          SELECT: {
            target: "active",
            cond: validSelection,
            actions: assign((context, event) => {
              console.log("Entry Active");
              if (event.type !== "SELECT") return context;

              const categoryIndex = context.finalizedCategories.indexOf(
                event.categoryName
              );

              let selectedBook: HydratedBook | null = null;

              const newBooksToSelectFrom = context.booksToSelectFrom.filter(
                (item) => {
                  if (item.id === event.bookId) {
                    selectedBook = item;
                    return false;
                  }
                  return true;
                }
              );

              if (!selectedBook) {
                throw new Error(
                  "Could not find selected book in booksToSelectFrom"
                );
              }

              const newUserSelections = [...context.userSelections];
              newUserSelections[categoryIndex].push(selectedBook);

              return {
                booksToSelectFrom: newBooksToSelectFrom,
                userSelections: newUserSelections,
              };
            }),
          },
        },
      },
      checkForFinish: {
        invoke: {
          src: "test",
          onDone: [
            { target: "sortCorrectAndIncorrect", cond: isGameOver },
            { target: "active" },
          ],
        },
      },
      active: {
        on: {
          SELECT: {
            target: "checkForFinish",
            cond: validSelection,
            actions: assign((context, event) => {
              console.log("Entry Active");
              if (event.type !== "SELECT") return context;

              const categoryIndex = context.finalizedCategories.indexOf(
                event.categoryName
              );

              let selectedBook: HydratedBook | null = null;

              const newBooksToSelectFrom = context.booksToSelectFrom.filter(
                (item) => {
                  if (item.id === event.bookId) {
                    selectedBook = item;
                    return false;
                  }
                  return true;
                }
              );

              if (!selectedBook) {
                throw new Error(
                  "Could not find selected book in booksToSelectFrom"
                );
              }

              const newUserSelections = [...context.userSelections];
              newUserSelections[categoryIndex].push(selectedBook);

              return {
                booksToSelectFrom: newBooksToSelectFrom,
                userSelections: newUserSelections,
              };
            }),
          },
        },
      },
      sortCorrectAndIncorrect: {
        invoke: {
          src: "sortAnswers",
          onDone: {
            target: "fetchReasons",
            actions: assign({
              answers: (_, event) => event.data.sortedAnswers,
            }),
            // actions: assign({
            //   score: (_, event) => event.data,
            // }),
          },
        },
      },
      fetchReasons: {
        invoke: {
          src: "requestReasonsFromOpenAI",
          onDone: [
            {
              target: "gameOver",
              cond: (context) => context.secondRoundHand.length === 0,
              actions: assign({
                answers: (_, event) => event.data.answersWithReasons, // Not actually sure I need this
                score: (_, event) => event.data.score,
                gameIsOver: true,
              }),
            },
            {
              target: "waitingRestart",
              actions: assign({
                answers: (_, event) => event.data.answersWithReasons,
                score: (_, event) => event.data.score,
              }),
            },
          ],
        },
      },
      waitingRestart: {
        on: {
          RESTART: {
            target: "initComplete",
            actions: [
              assign({
                booksToSelectFrom: (context) => context.secondRoundHand,
                secondRoundHand: [],
              }),
            ],
          },
        },
      },
      gameOver: {
        type: "final",
      },
    },
  },
  /**
   * TODOS:
   *
   * 1. Move a book to a selection (either moves from the booksToSelectFrom array to the userSelections array, or from one userSelections array to another)
   * 2. End game and check results.
   * 3. Display results.
   *
   * (hook it into the UI)
   */
  {
    services: {
      sortAnswers: async (context) => ({
        sortedAnswers: sortCorrectAndIncorrectAnswers(
          context.userSelections,
          context.booksCategorizedByOpenAI
        ),
      }),
      requestReasonsFromOpenAI: async (context) => {
        const scoreValue = context.secondRoundHand.length > 0 ? 1 : 2;
        const answersWithReasons = await getReasonsForIncorrectAnswers(
          context.booksCategorizedByOpenAI,
          context.answers,
          scoreValue
        );
        return {
          answersWithReasons,
          score: answersWithReasons.reduce((acc, curr) => acc + curr.score, 0),
        };
      },
      endGame: async () => true,
      test: async () => console.log("test invokes"),
      fetchBooks,
      sortBooks: (context) =>
        sortBooks(context.booksSelectedAtRandom, context.nytCategoryNames),
      selectRandomBooks: (context) =>
        selectRandomBooks(context.booksFetchedFromNYT, 8),
      hydrateBooksAndCategories: (context) =>
        hydrateBooksAndCategories(context.booksCategorizedByOpenAI),
      generateUserSelectionArrays: async (context) => {
        const userSelections = context.finalizedCategories.map((category) => {
          return [];
        });

        const booksToSelect = getRandomElements(
          context.finalizedBooks,
          context.finalizedBooks.length
        );

        const midpoint = Math.ceil(booksToSelect.length / 2);

        const firstHalf = booksToSelect.slice(0, midpoint);
        const secondHalf = booksToSelect.slice(midpoint);

        return {
          userSelections,
          booksToSelect: firstHalf,
          secondRoundHand: secondHalf,
        };
      },
    },
  }
);

// Instantiate the state machine
// const gameService = interpret(gameMachine)
//   .onTransition((state) => console.log(state.value))
//   .start();
